// ─── État des jetons, partagé (Core) ──────────────────────────────
// Un seul endroit calcule « qu'est-ce que j'ai le droit d'ouvrir » : la feuille
// de sélection et les écrans qui filtrent leurs éléments doivent répondre la
// même chose. Regroupe la dotation (plan + surcharge IVV), ma file ordonnée,
// et les jetons REÇUS — ces derniers vivant chez le donneur, seul le serveur
// peut les rassembler (`mesElementsConfies`).
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { usePeutUtiliser } from './FonctionsContext';
import { allouer, dotation as dotationDe } from './jetons';
import { mesElementsConfies } from './jetonsService';
import { useNbElementsPossedes } from './SourcesElementsContext';

export function useJetons() {
  const { user } = useAuth();   // (estIVV relu plus bas, avec la surcharge)
  const [cfg, setCfg] = useState({ plans: {} });
  const [recus, setRecus] = useState([]);

  useEffect(() => onSnapshot(doc(db, 'config', 'fonctionnalites'),
    (snap) => setCfg(snap.exists() ? { plans: snap.data().plans || {} } : { plans: {} }),
    () => {}), []);

  // Les dons reçus changent rarement : une lecture au montage (et au changement
  // de compte) suffit — pas d'écoute permanente sur toute la collection.
  useEffect(() => {
    if (!user?.uid) { setRecus([]); return; }
    mesElementsConfies().then(r => setRecus(r?.recus || [])).catch(() => setRecus([]));
  }, [user?.uid]);

  const selection = useMemo(
    () => (Array.isArray(user?.jetonsSelection) ? user.jetonsSelection : []),
    [user?.jetonsSelection]);

  const alloc = useMemo(() => allouer(cfg, user, selection, recus), [cfg, user, selection, recus]);
  const dotation = useMemo(() => dotationDe(cfg, user), [cfg, user]);
  // DEUX limites distinctes, à ne pas confondre :
  //   • VOIR  — au plus `dotation` éléments visibles à la fois ; retirer un
  //     élément de la liste libère un jeton pour en afficher un autre ;
  //   • POSSÉDER — on ne peut pas AVOIR plus d'éléments que le maximum de
  //     jetons. Retirer un élément de la liste des visibles ne libère donc pas
  //     un droit de création : il faut vraiment en supprimer un.
  // On peut se retrouver au-dessus (baisse de forfait) : on garde tout, mais on
  // n'en voit que le maximum autorisé.
  // NE PAS utiliser `peutCreerElement` directement dans un ecran : il ne sait pas
  // si le comptage est termine. La reponse officielle a « puis-je creer ? » est
  // `useQuotaElements()`, qui refuse tant qu'il n'a pas pu compter.
  const possedes = useNbElementsPossedes();
  const peutCreerElement = possedes < dotation.gestion;

  // Jetons ENCORE LIBRES : ce qui reste pour AFFICHER un élément de plus.
  const libres = useMemo(() => ({
    gestion: Math.max(0, dotation.gestion - selection.filter(x => x.nature === 'gestion').length),
    vue: Math.max(0, dotation.vue - selection.filter(x => x.nature === 'vue').length),
  }), [dotation, selection]);

  // Deux cas à ne PAS confondre (même piège que les filtres de l'admin) :
  //   • champ ABSENT = l'utilisateur n'a jamais rien choisi → on n'applique
  //     aucun filtre, sinon l'app se viderait d'un coup pour tous les comptes
  //     existants sans qu'ils aient rien demandé ;
  //   • tableau VIDE après enregistrement = choix explicite de tout écarter →
  //     le filtre s'applique, et l'écran est effectivement vide.
  // Sans la fonction « Éléments visibles », il n'y a pas de limite : rien n'est
  // masqué non plus.
  const peutJetons = usePeutUtiliser()('jetons');
  const jamaisEnregistre = !Array.isArray(user?.jetonsSelection);
  // Sans la fonction, aucune limite : tout reste visible. Un VALIDEUR peut en
  // outre couper la limite d'un geste (`ivvSansLimiteJetons`) — utile pour
  // comparer le comportement avec et sans quota, sans toucher a la dotation.
  const { estIVV } = useAuth();
  // Meme grammaire que les fonctions : une surcharge IVV n'est lue que si sa
  // bascule est ARMEE. Couper la limite est une surcharge parmi d'autres —
  // sans `ivvSurchargeJetons`, un ancien champ ne doit pas faire loi.
  const sansLimite = !peutJetons
    || (!!estIVV && user?.ivvSurchargeJetons === true && user?.ivvSansLimiteJetons === true);
  const droitDe = (cle) => (sansLimite ? 'gestion' : (alloc.actifs[cle] || null));

  // Rien d'enregistré : on ne montre pas tout pour autant — on applique la
  // règle « au plus ce que les jetons permettent » sur les N premiers éléments.
  // L'utilisateur voit donc immédiatement une app cohérente avec son forfait,
  // et sa première sélection remplace ce choix par défaut.
  const droitImplicite = (cles = []) => {
    const out = {};
    let g = dotation.gestion, v = dotation.vue;
    for (const cle of cles) {
      if (g > 0) { out[cle] = 'gestion'; g -= 1; }
      else if (v > 0) { out[cle] = 'vue'; v -= 1; }
    }
    return out;
  };

  return { cfg, selection, recus, alloc, dotation, libres, possedes, peutCreerElement, sansLimite, jamaisEnregistre, droitDe, droitImplicite };
}

export default useJetons;
